import React, { useCallback, useEffect, useRef } from "react";
import SITE from "@/constants/site";
import SITE_IDS from "@/constants/testIds/site";
import { useScrollVideo } from "@/hooks/useScrollVideo";
import { useReducedMotion } from "@/hooks/useEnvironment";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const seg = (p, a, b) => clamp((p - a) / (b - a), 0, 1);
const bell = (p, a, b, c, d) => Math.min(seg(p, a, b), 1 - seg(p, c, d));

// Inline style for a line of copy "emerging" from the card at `start` progress.
const revealStyle = (progress, reduced, start, dur = 0.15) => {
  // Reduced motion: show the full invitation statically (no scroll-driven motion).
  if (reduced) return { opacity: 1 };
  const t = easeOut(clamp((progress - start) / dur, 0, 1));
  return {
    opacity: t,
    transform: `translateY(${(1 - t) * 26}px)`,
    filter: `blur(${(1 - t) * 5}px)`,
  };
};

// Data-saver / 2G visitors get the poster instead of a 3.6MB film preload;
// the copy reveals still play on scroll.
const LITE_CONNECTION =
  typeof navigator !== "undefined" &&
  !!(navigator.connection &&
    (navigator.connection.saveData || /(^|-)2g$/.test(navigator.connection.effectiveType || "")));

function HeroVideo({ videoRef, reduced }) {
  return (
    <video
      ref={videoRef}
      data-testid={SITE_IDS.heroVideo}
      className="svh-video"
      poster={SITE.hero.posterStart}
      muted
      playsInline
      preload={LITE_CONNECTION ? "none" : "auto"}
      autoPlay={reduced && !LITE_CONNECTION}
      loop={reduced}
    >
      {/* Single all-intra H.264 source: every frame is a keyframe so
          scroll-scrub seeks are instant. (WebM/VP9 seeked too slowly.) */}
      <source src={SITE.hero.video} type="video/mp4" />
    </video>
  );
}

function HeroInvitation({ progress, reduced }) {
  // Reduced motion: the video loops freely, so give the copy a solid paper
  // backing and the classic centered layout instead of card-anchoring.
  if (reduced) {
    return (
      <div className="svh-card-copy svh-card-copy--reduced">
        <div className="svh-card-scrim" style={{ opacity: 1 }} aria-hidden="true" />
        <div className="svh-card-copy__inner">
          <span className="eyebrow eyebrow--dark">To our favorite people in the world</span>
          <h1 className="svh-names font-display">Thank You</h1>
          <p className="svh-request">Your love made our wedding unforgettable.</p>
          <div className="svh-date">
            <span className="font-display">
              We gathered every photo from the night. Come see it from every seat in the house.
            </span>
            <span className="svh-year">
              {SITE.first} <span className="svh-sign-amp">&amp;</span> {SITE.second}
            </span>
          </div>
        </div>
      </div>
    );
  }
  // The copy is printed on the film itself: one block, centered on the face
  // of the note that rises out of the envelope, anchored to video-content
  // coordinates (--vs/--voy) so it sits exactly on the paper at any size.
  return (
    <div className="svh-card-copy">
      <div className="svh-on-card">
        <span className="eyebrow eyebrow--dark" style={revealStyle(progress, reduced, 0.84, 0.08)}>
          To our favorite people in the world
        </span>
        <h1 className="svh-names font-display" style={revealStyle(progress, reduced, 0.87, 0.08)}>
          Thank You
        </h1>
        <p className="svh-request" style={revealStyle(progress, reduced, 0.9, 0.08)}>
          Your love made our wedding unforgettable.
        </p>
        <div className="svh-note-close" style={revealStyle(progress, reduced, 0.93, 0.06)}>
          <span className="font-display">
            We gathered every photo from the night. Come see it from every seat in the house.
          </span>
          <span className="svh-year">
            {SITE.first} <span className="svh-sign-amp">&amp;</span> {SITE.second}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ScrollVideoHero() {
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const stickyRef = useRef(null);
  const autoRaf = useRef(0);
  const reduced = useReducedMotion();
  const { progress } = useScrollVideo({ videoRef, trackRef, enabled: !reduced });

  // Tap-to-play: glide the page through the hero track so the film runs
  // on its own. The user grabbing the scroll cancels the ride instantly.
  const stopAutoScroll = useCallback(() => {
    if (autoRaf.current) cancelAnimationFrame(autoRaf.current);
    autoRaf.current = 0;
    // restore the page's default smooth scrolling once the ride ends
    document.documentElement.style.scrollBehavior = "";
  }, []);

  const playForMe = useCallback(() => {
    const track = trackRef.current;
    if (!track || reduced) return;
    const start = window.scrollY;
    const end = track.offsetTop + track.offsetHeight - window.innerHeight;
    const dist = end - start;
    if (dist <= 0) return;
    const duration = Math.min(14000, Math.max(4000, dist * 2.4));
    const t0 = performance.now();
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    stopAutoScroll();
    // The global `scroll-behavior: smooth` would re-animate every frame's
    // scrollTo and fight the tween — drive with instant positioning instead.
    document.documentElement.style.scrollBehavior = "auto";
    const step = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      window.scrollTo(0, start + dist * ease(p));
      if (p < 1) {
        autoRaf.current = requestAnimationFrame(step);
      } else {
        autoRaf.current = 0;
        document.documentElement.style.scrollBehavior = "";
      }
    };
    autoRaf.current = requestAnimationFrame(step);
  }, [reduced, stopAutoScroll]);

  // Map video-content coordinates (1280x720, object-fit: cover) to the
  // viewport so copy can be pinned to the card/envelope in the final frame.
  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return;
    const compute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const s = Math.max(w / 1280, h / 720);
      el.style.setProperty("--vs", s);
      el.style.setProperty("--voy", `${(h - 720 * s) / 2}px`);
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("orientationchange", compute);
    };
  }, []);

  useEffect(() => {
    const cancel = () => stopAutoScroll();
    window.addEventListener("wheel", cancel, { passive: true });
    window.addEventListener("touchstart", cancel, { passive: true });
    window.addEventListener("keydown", cancel);
    return () => {
      stopAutoScroll();
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchstart", cancel);
      window.removeEventListener("keydown", cancel);
    };
  }, [stopAutoScroll]);

  const midOpacity = reduced ? 0 : bell(progress, 0.30, 0.42, 0.56, 0.66);
  const cueOpacity = reduced ? 0 : 1 - seg(progress, 0.02, 0.12);

  return (
    <section id="top" data-testid={SITE_IDS.heroSection} className="svh-track" ref={trackRef}>
      <div className="svh-sticky" ref={stickyRef}>
        <HeroVideo videoRef={videoRef} reduced={reduced} />
        <div className="svh-vignette" aria-hidden="true" />
        <div className="svh-grain" aria-hidden="true" />

        {/* Mid cue */}
        <button
          type="button"
          className="svh-mid svh-cue-btn"
          style={{ opacity: midOpacity, pointerEvents: midOpacity < 0.1 ? "none" : "auto" }}
          onClick={playForMe}
        >
          <span className="svh-hint">Scroll to push</span>
        </button>

        {/* The copy — emerging from the card */}
        <HeroInvitation progress={progress} reduced={reduced} />

        {/* Scroll cue — tap plays the film without scrolling */}
        <button
          type="button"
          className="svh-cue svh-cue-btn"
          style={{ opacity: cueOpacity, pointerEvents: cueOpacity < 0.1 ? "none" : "auto" }}
          onClick={playForMe}
          aria-label="Play the film — scrolls the page for you"
        >
          <span className="svh-hint">Scroll</span>
          <span className="svh-cue__line" />
        </button>

        {/* Scrub progress bar */}
        <div className="svh-progress" aria-hidden="true">
          <div className="svh-progress__bar" style={{ transform: `scaleX(${progress})` }} />
        </div>
      </div>
    </section>
  );
}
